import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Loader2, Users } from 'lucide-react';
import { useReferralCodeValidation } from '@/hooks/useReferralCodeValidation';
import { cn } from '@/lib/utils';

interface ReferralCodeInputProps {
  value: string;
  onChange: (code: string) => void;
  onValidationChange?: (isValid: boolean, codeId: string | null, discountPercentage?: number | null) => void;
  initialValue?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  showLabel?: boolean;
}

export const ReferralCodeInput: React.FC<ReferralCodeInputProps> = ({
  value,
  onChange,
  onValidationChange,
  initialValue = '',
  autoFocus = false,
  disabled = false,
  label = 'Referral Code',
  placeholder = 'Enter referral code (optional)',
  showLabel = true,
}) => {
  const [inputValue, setInputValue] = useState(initialValue || value);
  const validation = useReferralCodeValidation(inputValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase();
    setInputValue(newValue);
    onChange(newValue);

    // Notify parent of validation changes
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.codeId, validation.discountPercentage);
    }
  };

  const showValidation = inputValue.trim().length > 0 && !validation.isLoading;

  return (
    <div className="space-y-2">
      {showLabel && (
        <Label htmlFor="referral-code" className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          {label}
        </Label>
      )}

      <div className="relative">
        <Input
          id="referral-code"
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          className={cn(
            "pr-10 uppercase",
            showValidation && validation.isValid && "border-green-500 focus-visible:ring-green-500",
            showValidation && !validation.isValid && "border-red-500 focus-visible:ring-red-500"
          )}
          maxLength={20}
        />

        {/* Validation indicator */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {validation.isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {showValidation && validation.isValid && (
            <CheckCircle className="h-4 w-4 text-green-600" />
          )}
          {showValidation && !validation.isValid && (
            <XCircle className="h-4 w-4 text-red-600" />
          )}
        </div>
      </div>

      {/* Validation message */}
      {showValidation && (
        <div className="space-y-1">
          {validation.isValid && validation.practitionerName && (
            <div className="space-y-1">
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Referred by <span className="font-medium">{validation.practitionerName}</span>
              </p>
              {validation.discountPercentage && validation.discountPercentage > 0 && (
                <p className="text-sm text-green-600 font-medium">
                  You'll get {validation.discountPercentage}% off your sessions!
                </p>
              )}
            </div>
          )}
          {!validation.isValid && validation.error && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              {validation.error}
            </p>
          )}
        </div>
      )}

      {/* Helper text when empty */}
      {inputValue.trim().length === 0 && !disabled && (
        <p className="text-xs text-muted-foreground">
          Have a referral code from a practitioner? Enter it here to get connected!
        </p>
      )}
    </div>
  );
};
